/**
 * New Competencies Achieved Trend Chart.
 * Same styling as CertificationsTrendChart / Tier1DRIProfilesTrendChart; shows new competencies per month (by competency_achieved_date).
 * Duration filter and month-wise x-axis; data from /dri-insights/competency-trend.
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { SEARCE_COLORS } from '../../theme';
import MonthRangePicker from '../MonthRangePicker';
import { dashboardApi } from '../../services/api';
import type { FilterRequest } from '../../types/api';
import type { CompetencyTrendRecord } from '../../types/driInsights';
import type { MonthRange } from '../../types/driInsights';
import { getDefaultCertificationMonths } from '../../utils/driInsightsUtils';
import { MONTH_NAMES, MONTH_ABBREVIATIONS } from '../../constants/driInsightsConstants';

interface MonthlyCompetencyData {
  month: string;
  year: number;
  count: number;
  monthAbbr: string;
  sortKey: string;
}

interface NewCompetenciesTrendChartProps {
  filters: FilterRequest | Record<string, unknown>;
  defaultStartMonth?: MonthRange;
  defaultEndMonth?: MonthRange;
  /** When 'my-team', uses /team-insights/my-team-competency-trend; otherwise /dri-insights/competency-trend */
  dataSource?: 'dri-insights' | 'my-team';
}

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: SEARCE_COLORS.cardBackground,
        border: `1px solid ${SEARCE_COLORS.border}`,
        borderRadius: 2,
        p: 1.5,
        boxShadow: `0 4px 12px ${alpha(SEARCE_COLORS.primary, 0.15)}`,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: SEARCE_COLORS.textPrimary }}>
        {label}
      </Typography>
      {payload.map((entry, index) => (
        <Typography
          key={index}
          variant="body2"
          sx={{
            color: entry.color,
            fontWeight: 500,
            mb: 0.5,
          }}
        >
          {`${entry.name}: ${entry.value.toLocaleString()}`}
        </Typography>
      ))}
    </Box>
  );
};

const NewCompetenciesTrendChart: React.FC<NewCompetenciesTrendChartProps> = ({
  filters,
  defaultStartMonth,
  defaultEndMonth,
  dataSource = 'dri-insights',
}) => {
  const defaultMonths = getDefaultCertificationMonths();
  const [startMonth, setStartMonth] = useState<MonthRange>(
    defaultStartMonth ?? defaultMonths.startMonth
  );
  const [endMonth, setEndMonth] = useState<MonthRange>(
    defaultEndMonth ?? defaultMonths.endMonth
  );
  const [selectedCompetency, setSelectedCompetency] = useState('');

  // Follow the caller's range: when the page's Duration filter moves, adopt it. The chart's
  // own Duration picker still overrides locally until the main filter changes again.
  // Depends on the values, not the object identity, so a re-rendered parent doesn't clobber
  // a local pick. DRI Insights passes neither prop, so it keeps its own default untouched.
  useEffect(() => {
    if (defaultStartMonth) setStartMonth(defaultStartMonth);
    if (defaultEndMonth) setEndMonth(defaultEndMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultStartMonth?.year, defaultStartMonth?.month, defaultEndMonth?.year, defaultEndMonth?.month]);

  const isMyTeam = dataSource === 'my-team';
  const { data: competencyRecords = [], isLoading } = useQuery({
    queryKey: [isMyTeam ? 'my-team-competency-trend' : 'dri-insights-competency-trend', filters],
    queryFn: () =>
      isMyTeam
        ? dashboardApi.getMyTeamCompetencyTrend(filters as Record<string, unknown>)
        : dashboardApi.getDriInsightsCompetencyTrend(filters as FilterRequest),
    enabled: isMyTeam
      ? true
      : !!(
          (filters as FilterRequest).business_units?.length ||
          (filters as FilterRequest).sub_bus?.length
        ),
    staleTime: 5 * 60 * 1000,
  });

  const competencyOptions = useMemo(() => {
    const set = new Set<string>();
    competencyRecords.forEach((r) => {
      const c = typeof r.competency === 'string' ? r.competency.trim() : '';
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [competencyRecords]);

  useEffect(() => {
    if (selectedCompetency && !competencyOptions.includes(selectedCompetency)) {
      setSelectedCompetency('');
    }
  }, [competencyOptions, selectedCompetency]);

  const competencyRecordsFiltered = useMemo(() => {
    if (!selectedCompetency) return competencyRecords;
    return competencyRecords.filter(
      (r) => typeof r.competency === 'string' && r.competency.trim() === selectedCompetency
    );
  }, [competencyRecords, selectedCompetency]);

  const chartData = useMemo((): MonthlyCompetencyData[] => {
    const start = startMonth ?? defaultMonths.startMonth;
    const end = endMonth ?? defaultMonths.endMonth;
    if (!start || !end) return [];

    const targetStartYear = start.year;
    const targetStartMonth = start.month;
    const targetEndYear = end.year;
    const targetEndMonth = end.month;

    const months: { month: string; year: number; monthAbbr: string; sortKey: string }[] = [];
    let currentYear = targetStartYear;
    let currentMonth = targetStartMonth;

    while (
      currentYear < targetEndYear ||
      (currentYear === targetEndYear && currentMonth <= targetEndMonth)
    ) {
      months.push({
        month: MONTH_NAMES[currentMonth - 1],
        year: currentYear,
        monthAbbr: `${MONTH_ABBREVIATIONS[currentMonth - 1]} ${currentYear}`,
        sortKey: `${currentYear}-${String(currentMonth).padStart(2, '0')}`,
      });
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    return months.map((monthData) => {
      const targetMonthIndex = MONTH_NAMES.indexOf(monthData.month) + 1;
      const count = competencyRecordsFiltered.filter((record: CompetencyTrendRecord) => {
        const dateStr = record.competency_achieved_date;
        if (!dateStr) return false;
        const d = new Date(dateStr);
        const y = d.getFullYear();
        const m = d.getMonth() + 1;
        return y === monthData.year && m === targetMonthIndex;
      }).length;
      return { ...monthData, count };
    });
  }, [competencyRecordsFiltered, startMonth, endMonth, defaultMonths.startMonth, defaultMonths.endMonth]);

  const handleMonthRangeChange = (start: MonthRange, end: MonthRange) => {
    setStartMonth(start);
    setEndMonth(end);
  };

  const showEmpty = !startMonth || !endMonth || isLoading;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.1)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', width: '100%', position: 'relative', overflow: 'hidden', minHeight: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary, mb: 2 }}>
          New Competencies Achieved Trend
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1.5,
            mb: 1.5,
            flexShrink: 0,
            flexWrap: 'wrap',
          }}
        >
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="competency-trend-filter-label">Competency</InputLabel>
            <Select
              labelId="competency-trend-filter-label"
              label="Competency"
              value={selectedCompetency}
              onChange={(e) => setSelectedCompetency(e.target.value)}
              disabled={isLoading}
              sx={{
                fontSize: '0.75rem',
                height: '40px',
                backgroundColor: SEARCE_COLORS.white,
                '& .MuiSelect-select': { py: 0.75 },
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
                All
              </MenuItem>
              {competencyOptions.map((name) => (
                <MenuItem key={name} value={name} sx={{ fontSize: '0.75rem' }}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ minWidth: 150 }}>
            <MonthRangePicker
              startMonth={startMonth}
              endMonth={endMonth}
              onChange={handleMonthRangeChange}
              label="Duration"
            />
          </Box>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          {showEmpty ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
                {!startMonth || !endMonth
                  ? 'Please select a date range to view new competencies trend'
                  : 'Loading…'}
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 15, left: 5, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(SEARCE_COLORS.neutral_300, 0.3)} />
                <XAxis
                  dataKey="monthAbbr"
                  tick={{ fontSize: 9, fill: SEARCE_COLORS.textSecondary }}
                  axisLine={{ stroke: SEARCE_COLORS.border }}
                  tickLine={false}
                  angle={chartData.some((item) => item.year) ? -45 : 0}
                  textAnchor={chartData.some((item) => item.year) ? 'end' : 'middle'}
                  height={chartData.some((item) => item.year) ? 50 : 25}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: SEARCE_COLORS.textSecondary }}
                  axisLine={{ stroke: SEARCE_COLORS.border }}
                  tickLine={false}
                  width={40}
                />
                <RechartsTooltip
                  content={<CustomTooltip />}
                  allowEscapeViewBox={{ x: false, y: false }}
                  wrapperStyle={{ zIndex: 9999, pointerEvents: 'none' }}
                  contentStyle={{ zIndex: 9999, pointerEvents: 'none' }}
                />
                <Legend
                  wrapperStyle={{
                    fontSize: '10px',
                    fontWeight: 500,
                    paddingTop: '3px',
                  }}
                  iconType="line"
                  iconSize={10}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke={SEARCE_COLORS.primary}
                  strokeWidth={2}
                  dot={{ fill: SEARCE_COLORS.primary, strokeWidth: 1.5, r: 3 }}
                  activeDot={{ r: 5, stroke: SEARCE_COLORS.primary, strokeWidth: 2 }}
                  name={
                    selectedCompetency
                      ? `New Competencies Achieved (${selectedCompetency})`
                      : 'New Competencies Achieved'
                  }
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default NewCompetenciesTrendChart;
